import React from 'react';
import Modal from './Modal';
import Button from './Button';

/**
 * Componente para mostrar un diálogo de confirmación
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Si el diálogo está abierto
 * @param {function} props.onClose - Función para cerrar el diálogo
 * @param {function} props.onConfirm - Función a ejecutar al confirmar
 * @param {string} props.title - Título del diálogo
 * @param {ReactNode} props.message - Mensaje a mostrar
 * @param {string} props.confirmText - Texto del botón de confirmación
 * @param {string} props.cancelText - Texto del botón de cancelación
 * @param {string} props.confirmVariant - Variante del botón de confirmación
 * @param {string} props.icon - Icono opcional para el diálogo
 * @returns {JSX.Element} - Componente ConfirmDialog
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = '¿Estás seguro?',
  message = 'Esta acción no se puede deshacer.',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  confirmVariant = 'danger',
  icon = null
}) => {
  // Manejar confirmación
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // Footer personalizado con botones
  const dialogFooter = (
    <div className="flex justify-end gap-2">
      <Button 
        variant="secondary"
        onClick={onClose}
      >
        {cancelText}
      </Button>
      <Button 
        variant={confirmVariant}
        onClick={handleConfirm}
      >
        {confirmText}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={dialogFooter}
    >
      <div className="flex flex-col items-center text-center">
        {icon && (
          <div className="mb-4 text-4xl">
            {icon}
          </div>
        )}
        <p className="text-gray-300">
          {message}
        </p>
      </div>
    </Modal>
  );
};

/**
 * Icono de advertencia para usar con el diálogo
 */
export const WarningIcon = () => (
  <div className="text-yellow-500 w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-yellow-100 bg-opacity-20">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  </div>
);

/**
 * Icono de eliminación para usar con el diálogo
 */
export const DeleteIcon = () => (
  <div className="text-red-500 w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-red-100 bg-opacity-20">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  </div>
);

export default ConfirmDialog;